import uuidV4 from 'lib/string/uuidV4Utils'
import whitelist from 'lib/payment/whitelist'

import {
  SAVE_BOOKING_STATE,
  ACCEPT_INSURANCE_QUOTE,
  REJECT_INSURANCE_QUOTE,
  RESET_CART_QUOTE,
  API_CALL_SUCCESS,
  CART_EMPTY,
  CART_EMPTY_CHANGE_PACKAGE,
  SET_INSURANCE_PRODUCT,
  SET_INSURANCE_TRAVELLERS,
  RESET_CART_INSURANCE,
  SET_INSURANCE_ORDER,
  CART_CHANGE_OFFER_PACKAGE,
  CART_ADD_ORDER,
  CART_ADD_BEDBANK,
  CART_PROCESS_START,
  CART_PROCESS_CANCEL,
  CART_PROCESS_SUCCESS,
  CART_ORDER_PROCESS_FAILURE, CART_REMOVE,
  UPDATE_PERSONALISE_GIFT_CARD_DATA,
  SET_LE_CREDIT_BALANCE,
  TOGGLE_LE_CREDIT,
  SET_BOOK_DATES_FOR_ORDER,
  SET_CHANGE_DATES_FOR_ORDER,
  SET_CHANGE_PACKAGE_FOR_ORDER,
  REMOVE_PROMO_CODE,
  APPLY_PROMO_CODE,
  CREDIT_UPDATE,
  ACTIVATE_REBOOKING,
  UPDATE_DEPARTING_FLIGHT,
  UPDATE_RETURNING_FLIGHT,
  UPDATE_FLIGHT_PRICE_BREAKDOWN,
  UPDATE_FLIGHT_JOURNEY_COSTS,
  UPDATE_FLIGHT_JOURNEY_COST_WITH_BOOKING_FEE,
  UPDATE_FLIGHT_JOURNEY_COST_WITH_SERVICE_FEE,
  UPDATE_FLIGHT_JOURNEY_COST_WITH_FLIGHTS_CREDIT_FEE,
  UPDATE_FLIGHT_JOURNEY_COST_WITH_ATOL_FEE,
  UPDATE_BASE_PRICE_SNAPSHOT,
  START_FLIGHT_CHECKOUT,
  RESET_FLIGHTS,
  RESET_DEPARTING_FLIGHT,
  RESET_RETURNING_FLIGHT,
  UPDATE_REFERRAL_CODE,
  UPDATE_PASSENGER_BAGGAGE,
  UPDATE_TWO_WAY_PASSENGER_BAGGAGE,
  CART_UPDATE_RESERVATION,
  CART_PROCESS_FAILURE,
  CART_UPDATE_ALL_RESERVATIONS,
  RESET_CART_ORDER,
  CART_PACKAGE_UPSELL_SHOWN,
  CART_ADD_GIFT_CARD,
  CART_SET_OFFER,
  CART_ADD_PACKAGE,
  CART_SET_CURRENCY,
  CART_UPDATE_RESERVATION_TYPE,
  API_CALL_REQUEST,
  API_CALL_FAILURE,
  CART_INITIALISED,
  CART_SET_RESERVATION_DATES,
  CART_RESET_RESERVATION_DATES,
  CART_UPDATE_ACCOMMODATION_PRICE,
  CART_UPDATE_FLIGHT_ORIGIN,
  CART_UPDATE_FLIGHT_DESTINATION,
  CART_UPDATE_OCCUPANCY,
  CART_SET_GIFT,
  CART_UPDATE_GIFT_FORM,
  RESET_FLIGHT_SELECT,
  FLIGHT_JOURNEY_COSTS_UPDATED,
  UPDATE_PAYMENT_SELECTION,
  CART_SET_EXPERIENCE_ITEM,
  CART_SET_FLIGHTS_CREDIT_DATA,
  CART_ADD_PAYMENT_BALANCE,
  CART_SET_FLIGHTS_AVAILABILITY,
  UPDATE_DEPARTING_FLIGHT_SEGMENT_COST,
  UPDATE_RETURNING_FLIGHT_SEGMENT_COST,
  CART_SET_EXPERIENCE_TRANSFER,
  CART_REMOVE_EXPERIENCE_TRANSFER,
} from 'actions/actionConstants'
import { arrayToMap, without } from 'lib/array/arrayUtils'

import {
  FETCH_INSURANCE_QUOTES,
  INITIALISING_CART,
  FETCH_AVAILABLE_CREDIT,
  USER_LOGOUT,
  CART_RESTORE,
  FETCH_PACKAGE_PRICES,
  GET_FINAL_FLIGHT_FARES,
  FETCH_FLIGHT_BAGGAGE,
  FETCH_BEDBANK_OFFER_RATES,
  CART_UPDATE_BEDBANK_SESSION_ID,
  FETCH_EXPERIENCE_CHECKOUT_TRAVELLER_FIELDS,
  FETCH_TAX_BREAKDOWN,
} from 'actions/apiActionConstants'
import { reducerSwitch, createReducer } from 'lib/redux/reducerUtils'
import { ApiAction } from 'middlewares/apiMiddleware'
import { mapObject } from 'lib/object/objectUtils'
import { PAYMENT_OPTIONS } from 'constants/payment'

// Reset Transaction Key Middleware
//
// The intent of this middleware is to reset all the transaction keys on the
// order if an error is returned from server requiring the user to make a change.
//
// All actions that are the result of user interaction should call this function
// with the new state.
//

const resetTransactionKey = <T extends { transactionKey: string }>(x: T, transactionKeyOverride?: string): T => ({ ...x, transactionKey: transactionKeyOverride ?? uuidV4() })

function resetTransaction(
  state: App.CartState,
  opts: { preserveTransactionKeys: boolean } = { preserveTransactionKeys: false },
): Partial<App.CartState> {
  return {
    ...resetTransactionKey(state, opts.preserveTransactionKeys ? state.transactionKey : undefined),
    giftCard: resetTransactionKey(state.giftCard),
    items: state.items.map((item) => resetTransactionKey(item, opts.preserveTransactionKeys ? item.transactionKey : undefined)),
    bedbank: resetTransactionKey(state.bedbank),
    flight: resetTransactionKey(state.flight),
    insurance: resetTransactionKey(state.insurance),
    experiences: {
      ...state.experiences,
      items: mapObject(state.experiences.items, item => resetTransactionKey(item)),
      transfers: mapObject(state.experiences.transfers, transfer => ({
        ...transfer,
        toHotel: transfer.toHotel ? resetTransactionKey(transfer.toHotel) : undefined,
        toAirport: transfer.toAirport ? resetTransactionKey(transfer.toAirport) : undefined,
      })),
    },
  }
}

const getFlightDefaultState = (): App.CartFlight => ({
  flightOrigin: undefined,
  flightDestination: undefined,
  flightOriginName: undefined,
  flightDestinationName: undefined,
  transactionKey: uuidV4(),
  contactPhoneNumber: '',
  contactEmailAddress: '',
  basePriceSnapshot: 0,
  departingPriceSnapshot: 0,
  journey: {
    id: '',
    itemType: 'journeyV1',
    cost: 0,
    tax: 0,
    validatingCarrier: '',
    departing: {
      journeyKey: '',
      flights: [],
    },
    returning: {
      journeyKey: '',
      flights: [],
    },
    provider: undefined,
    isAllianceCarrier: false,
  },
  // populated at CHECKOUT_START
  passengers: [],
  creditDetails: null,
})

const getCartInsuranceInitialState = (): App.CartInsurance => ({
  transactionKey: uuidV4(),
  insuranceAccepted: false,
  insuranceProduct: undefined,
  insuranceQuote: undefined,
  insuranceTravellers: [],
  insuranceProductId: undefined,
  destinationCountries: [],
  insuranceEndDate: undefined,
  insuranceStartDate: undefined,
  insuranceOrderId: undefined,
  numberOfSeniors: 0,
})

const getCartAccommodationInitialState = (): App.CartAccommodation => ({
  package: undefined,
  offer: undefined,
  originalPackageKey: undefined,
  packageUpsellShown: false,
  reservationType: undefined,
  occupancies: [],
  startDate: undefined,
  endDate: undefined,
  cancellationPolicies: undefined,
  taxBreakdown: {},
  availableRooms: undefined,
  flightsIncluded: false,
})

const getCartGiftCardInitialState = (): App.CartGiftCard => ({
  transactionKey: uuidV4(),
  personalisation: {
    method: 'print',
    to: '',
    from: '',
    message: '',
    email_of_recipient: '',
    is_blank_message: false,
  },
  price: 0,
})

const getCartPaymentBalanceInitialState = (): App.CartPaymentBalance => ({
  transactionKey: uuidV4(),
  price: 0,
})

const bedbankItemInitialState = (): App.CartBedbank => ({
  transactionKey: uuidV4(),
  rate: null,
  package: null,
  offer: null,
  sessionId: null,
  bedGroupId: null,
  bookingEmail: null,
  bookingPhonePrefix: null,
  bookingPhonePrefixCountryCode: null,
  bookingPhone: null,
  checkIn: null,
  checkOut: null,
  price: 0,
  rooms: [],
})

const experiencesInitialState = (): App.CartExperiences => ({
  items: {},
  transfers: {},
  travellerForms: { },
  unavailableTickets: {},
  customerDetails: {},
  travellerDetails: [],
})

export const initialState: App.CartState = {
  transactionKey: uuidV4(),
  items: [],
  experiences: experiencesInitialState(),
  bedbank: bedbankItemInitialState(),
  insurance: getCartInsuranceInitialState(),
  flight: getFlightDefaultState(),
  error: undefined,
  initialised: false,
  initialiseError: undefined,
  processing: false,
  processed: false,
  order: undefined,
  accommodation: getCartAccommodationInitialState(),
  fetchingPackagePrices: false,
  useLeCredit: false,
  usePromo: false,
  bookDatesOnly: false,
  isChangeDates: false,
  isChangePackage: false,
  promoName: '',
  currencyCode: null,
  referral: null,
  cartRestoring: false,
  restored: false,
  giftCard: getCartGiftCardInitialState(),
  paymentBalance: getCartPaymentBalanceInitialState(),
  promo: {
    id: null,
    code: null,
    discount: 0,
    maxDiscount: undefined,
    type: null,
    warning: null,
    currency: null,
    expiresAt: null,
    allowedPaymentMethods: [],
    allowedAirlineCarriers: [],
    items: [],
    hasBinNumbers: false,
    hideDiscountPercentage: false,
  },
  credit: {
    currencyCode: null,
    balance: 0,
    error: null,
  },
  cardProcessingErrorCount: 0,
  isFlightSelectLoading: false,
  isFlightSelectError: false,
  isFlightSelected: false,
  journeyCostUpdated: false,
  fetchingTaxBreakdown: false,
  rebookingID: null,
  paymentSelected: PAYMENT_OPTIONS.FULL,
  isGift: false,
  gift: null,
}

function setCreditBalance(state, currencyCode, balance, error) {
  if (error) {
    return {
      credit: {
        currencyCode: state.currencyCode,
        balance: 0,
        error,
      },
    }
  } else if (currencyCode !== state.currencyCode) {
    return {
      credit: {
        currencyCode: state.currencyCode,
        balance: 0,
        error: 'Currency mismatch',
      },
    }
  } else {
    return {
      credit: {
        currencyCode,
        balance,
        error: null,
      },
    }
  }
}

function removeBaggageForUnselectedJourneys(state) {
  const selectedJourneys = [
    state.flight.journey.departing.journeyKey,
    state.flight.journey.returning.journeyKey,
  ].filter(key => !!key)

  return {
    flight: {
      ...state.flight,
      passengers: state.flight.passengers.map(p => {
        if (!p.addedExtras?.baggage) {
          return p
        }

        return {
          ...p,
          addedExtras: {
            ...p.addedExtras,
            baggage: whitelist(p.addedExtras.baggage, selectedJourneys),
          },
        }
      }),
    },
  }
}

export default createReducer<App.CartState>(initialState, {
  [CART_ADD_ORDER]: (state, action) => ({
    order: action.data,
  }),
  [CART_PROCESS_START]: () => ({
    error: undefined,
    processing: true,
    processed: false,
  }),
  [CART_PROCESS_CANCEL]: (state) => resetTransaction({
    ...state,
    error: undefined,
    processing: false,
    processed: false,
  }),
  [CART_PROCESS_SUCCESS]: (state, action) => resetTransaction({
    ...initialState,
    order: action.order,
    processing: false,
    processed: true,
    journeyCostUpdated: false,
  }),
  [CART_PROCESS_FAILURE]: (state, action) => ({
    error: action.data,
    processing: false,
    processed: false,
    cardProcessingErrorCount: action.data.cardProcessingErrorCount,
  }),
  [CART_ORDER_PROCESS_FAILURE]: (state) => resetTransaction({ ...state }),
  [CART_EMPTY]: () => resetTransaction({ ...initialState }),
  [CART_EMPTY_CHANGE_PACKAGE]: () => resetTransaction({ ...initialState, isChangePackage: true }),
  [CART_SET_OFFER]: (state, action) => resetTransaction({
    ...state,
    accommodation: {
      ...state.accommodation,
      offer: action.offer,
      package: action.package,
      reservationType: action.reservationType,
      originalPackageKey: action.package.uniqueKey,
      cancellationPolicies: action.cancellationPolicies,
      availableRooms: action.availableRooms,
    },
  }),
  [CART_ADD_PACKAGE]: (state, action) => resetTransaction({
    ...state,
    items: [
      ...state.items,
      action.item,
    ],
    accommodation: {
      ...state.accommodation,
      occupancies: [
        ...state.accommodation.occupancies,
        action.occupancy,
      ],
    },
  }),
  [CART_UPDATE_OCCUPANCY]: (state, action) => {
    const newOccupancies = [...state.accommodation.occupancies]
    newOccupancies[action.roomIndex] = action.occupancy

    return {
      accommodation: {
        ...state.accommodation,
        occupancies: newOccupancies,
      },
    }
  },
  [CART_SET_GIFT]: (state, action) => ({
    isGift: action.value,
  }),
  [CART_UPDATE_GIFT_FORM]: (state, action) => ({
    gift: action.form,
  }),
  [CART_SET_CURRENCY]: (state, action) => ({
    currencyCode: action.currency,
  }),
  [CART_REMOVE]: (state, action) => {
    const itemIndex = state.items.findIndex(item => item.id === action.id)
    return resetTransaction({
      ...state,
      items: state.items.filter(item => item.id !== action.id),
      accommodation: {
        ...state.accommodation,
        occupancies: without(state.accommodation.occupancies, state.accommodation.occupancies[itemIndex]),
      },
    })
  },
  [CART_RESET_RESERVATION_DATES]: (state) => ({
    insurance: getCartInsuranceInitialState(),
    flight: {
      ...state.flight,
      basePriceSnapshot: 0,
    },
    accommodation: {
      ...state.accommodation,
      startDate: undefined,
      endDate: undefined,
    },
    items: state.items.map(item => ({
      ...item,
      reservation: {
        ...item.reservation,
        durationSurchargeTotal: 0,
      },
    })),
  }),
  [CART_SET_RESERVATION_DATES]: (state, action) => ({
    insurance: getCartInsuranceInitialState(),
    accommodation: {
      ...state.accommodation,
      startDate: action.checkIn,
      endDate: action.checkOut,
    },
  }),
  [CART_SET_FLIGHTS_AVAILABILITY]: (state, action) => ({
    accommodation: {
      ...state.accommodation,
      flightsIncluded: action.isFlightsAvailable,
    },
  }),
  [CART_UPDATE_RESERVATION]: (state, action) => ({
    items: state.items.map(item => ({
      ...item,
      reservation: {
        ...item.reservation,
        ...(item.id === action.id ? action.data : {}),
      },
    })),
  }),
  [CART_UPDATE_ALL_RESERVATIONS]: (state, action) => ({
    items: state.items.map(item => ({
      ...item,
      reservation: {
        ...item.reservation,
        ...action.data,
      },
    })),
  }),
  [CART_UPDATE_RESERVATION_TYPE]: (state, action) => ({
    items: state.items.map(item => ({
      ...item,
      reservation: {
        ...item.reservation,
        durationSurchargeTotal: undefined,
      },
    })),
    flight: getFlightDefaultState(),
    insurance: getCartInsuranceInitialState(),
    accommodation: {
      ...state.accommodation,
      reservationType: action.reservationType,
      startDate: undefined,
      endDate: undefined,
    },
  }),
  [CART_CHANGE_OFFER_PACKAGE]: (state, action) => ({
    accommodation: {
      ...state.accommodation,
      package: action.package,
    },
    items: state.items.map(item => ({
      ...item,
      price: action.package.price,
      value: action.package.value,
    })),
    insurance: getCartInsuranceInitialState(),
    flight: getFlightDefaultState(),
  }),
  [SET_LE_CREDIT_BALANCE]: (state, action) => setCreditBalance(state, action.data.currencyCode, action.data.balance, action.data.error),
  [TOGGLE_LE_CREDIT]: (state, action) => ({
    useLeCredit: action.data.useLeCredit,
  }),
  [SET_BOOK_DATES_FOR_ORDER]: (state, action) => resetTransaction({
    ...state,
    bookDatesOnly: true,
    order: action.data.order,
  }),
  [SET_CHANGE_DATES_FOR_ORDER]: (state, action) => ({
    isChangeDates: true,
    order: action.data.order,
  }),
  [SET_CHANGE_PACKAGE_FOR_ORDER]: (state, action) => ({
    isChangePackage: true,
    order: action.data.order,
  }),
  [REMOVE_PROMO_CODE]: () => ({
    promo: { ...initialState.promo },
  }),
  [APPLY_PROMO_CODE]: (state, action) => ({
    promo: action.data,
  }),
  [ACTIVATE_REBOOKING]: (state, action) => ({
    rebookingID: action.data.rebookingID,
  }),
  [CREDIT_UPDATE]: (state, action) => {
    if (action.data.currencyCode === state.currencyCode) {
      return {
        credit: {
          currencyCode: action.data.currencyCode,
          balance: action.data.balance,
          error: null,
        },
      }
    }
  },
  [UPDATE_DEPARTING_FLIGHT]: (state, action) => {
    const updateJourney = action.data as App.Journey
    const newState = {
      flight: {
        ...state.flight,
        departingPriceSnapshot: updateJourney.cost,
        journey: {
          ...state.flight.journey,
          ...updateJourney,
        },
      },
    }

    return removeBaggageForUnselectedJourneys(newState)
  },
  [UPDATE_RETURNING_FLIGHT]: (state, action) => {
    const updateJourney = action.data as App.Journey
    const newState = {
      flight: {
        ...state.flight,
        journey: {
          ...state.flight.journey,
          ...updateJourney,
        },
      },
    }

    return removeBaggageForUnselectedJourneys(newState)
  },
  [UPDATE_FLIGHT_PRICE_BREAKDOWN]: (state, action) => ({
    flight: {
      ...state.flight,
      priceBreakdown: action.data,
    },
  }),
  [UPDATE_FLIGHT_JOURNEY_COSTS]: (state, action) => resetTransaction({
    ...state,
    flight: {
      ...state.flight,
      journey: {
        ...state.flight.journey,
        ...action.data,
      },
    },
  }, { preserveTransactionKeys: true }),
  [UPDATE_FLIGHT_JOURNEY_COST_WITH_FLIGHTS_CREDIT_FEE]: (state, action) => {
    const totalCost = state.flight.journey.cost + action.data
    return resetTransaction({
      ...state,
      flight: {
        ...state.flight,
        journey: {
          ...state.flight.journey,
          cost: totalCost,
          flightsCreditFee: action.data,
        },
      },
    }, { preserveTransactionKeys: true })
  },
  [UPDATE_FLIGHT_JOURNEY_COST_WITH_BOOKING_FEE]: (state, action) => {
    const totalCost = state.flight.journey.cost + action.data
    return resetTransaction({
      ...state,
      flight: {
        ...state.flight,
        journey: {
          ...state.flight.journey,
          cost: totalCost,
          bookingFee: action.data,
        },
      },
    }, { preserveTransactionKeys: true })
  },
  [UPDATE_FLIGHT_JOURNEY_COST_WITH_SERVICE_FEE]: (state, action) => {
    const { serviceFee } = action.data

    const totalCost = state.flight.journey.cost + serviceFee
    return resetTransaction({
      ...state,
      flight: {
        ...state.flight,
        journey: {
          ...state.flight.journey,
          cost: totalCost,
          serviceFee,
        },
      },
    }, { preserveTransactionKeys: true })
  },
  [UPDATE_FLIGHT_JOURNEY_COST_WITH_ATOL_FEE]: (state, action) => {
    const totalCost = state.flight.journey.cost + action.data
    return resetTransaction({
      ...state,
      flight: {
        ...state.flight,
        journey: {
          ...state.flight.journey,
          cost: totalCost,
          atolFee: action.data,
        },
      },
    }, { preserveTransactionKeys: true })
  },
  [FLIGHT_JOURNEY_COSTS_UPDATED]: (state, action) => ({
    ...state,
    journeyCostUpdated: action.data,
  }),
  [UPDATE_PAYMENT_SELECTION]: (state, action) => ({
    ...state,
    paymentSelected: action.data,
  }),
  [UPDATE_BASE_PRICE_SNAPSHOT]: (state, action) => ({
    flight: {
      ...state.flight,
      basePriceSnapshot: action.data,
    },
  }),
  [START_FLIGHT_CHECKOUT]: (state, action) => ({
    flight: {
      ...state.flight,
      passengers: action.passengers,
    },
  }),
  [RESET_FLIGHTS]: (state) => ({
    flight: {
      ...state.flight,
      journey: getFlightDefaultState().journey,
    },
  }),
  [RESET_DEPARTING_FLIGHT]: (state) => ({
    flight: {
      ...state.flight,
      journey: {
        ...state.flight.journey,
        departing: getFlightDefaultState().journey.departing,
      },
    },
  }),
  [RESET_RETURNING_FLIGHT]: (state) => ({
    flight: {
      ...state.flight,
      journey: {
        ...state.flight.journey,
        returning: getFlightDefaultState().journey.returning,
      },
    },
  }),
  [UPDATE_REFERRAL_CODE]: (state, action) => ({
    referral: action.data.referralCode,
  }),
  [SAVE_BOOKING_STATE]: (state, action) => {
    const {
      passengers,
      insurance,
      bedbank,
      experienceCustomerDetails,
      experienceTravellerDetails,
      gift,
    } = action.data

    let passengerMap
    if (passengers) {
      passengerMap = arrayToMap<string, App.Passenger>(passengers, p => p.id)
    }

    return {
      gift,
      insurance: insurance ? {
        ...state.insurance,
        ...insurance,
      } : state.insurance,
      flight: passengerMap ? {
        ...state.flight,
        passengers: state.flight.passengers.map(p => ({
          ...p,
          ...passengerMap.get(p.id),
        })),
      } : state.flight,
      bedbank: bedbank ? {
        ...state.bedbank,
        bedGroupId: bedbank.bedGroupId,
        bookingEmail: bedbank.bookingEmail,
        bookingPhone: bedbank.bookingPhone,
        bookingPhonePrefix: bedbank.bookingPhonePrefix,
        rooms: state.bedbank.rooms.map((room, index) => (
          {
            ...room,
            ...bedbank.guests[index + 1],
          }
        )),
      } : state.bedbank,
      experiences: {
        ...state.experiences,
        customerDetails: experienceCustomerDetails ?? {},
        travellerDetails: experienceTravellerDetails ?? {},
      },
    }
  },
  [UPDATE_PASSENGER_BAGGAGE]: (state, action) => ({
    flight: {
      ...state.flight,
      passengers: state.flight.passengers.map((p, i) => ({
        ...p,
        addedExtras: {
          ...p.addedExtras,
          baggage: {
            ...p.addedExtras.baggage,
            [action.data.journeyId]: action.data.baggage[i],
          },
        },
      })),
    },
  }),
  [UPDATE_TWO_WAY_PASSENGER_BAGGAGE]: (state, action) => ({
    flight: {
      ...state.flight,
      passengers: state.flight.passengers.map(p => {
        if (p.type !== 'infant') {
          return {
            ...p,
            addedExtras: {
              ...p.addedExtras,
              baggage: {
                ...p.addedExtras.baggage,
                [action.data.journeyId]: action.data.baggage,
              },
            },
          }
        }
        return p
      }),
    },
  }),
  [ACCEPT_INSURANCE_QUOTE]: (state, action) => ({
    insurance: {
      ...state.insurance,
      insuranceQuote: action.quote,
      insuranceAccepted: true,
    },
  }),
  [REJECT_INSURANCE_QUOTE]: (state) => ({
    insurance: {
      ...state.insurance,
      insuranceAccepted: false,
    },
  }),
  [RESET_CART_QUOTE]: (state) => ({
    insurance: {
      ...state.insurance,
      insuranceAccepted: false,
      insuranceQuote: undefined,
    },
  }),
  [SET_INSURANCE_PRODUCT]: (state, action) => ({
    insurance: {
      ...state.insurance,
      insuranceProductId: action.productId,
      insuranceProduct: action.product,
    },
  }),
  [SET_INSURANCE_TRAVELLERS]: (state, action) => ({
    insurance: {
      ...state.insurance,
      insuranceTravellers: action.travellers,
    },
  }),
  [SET_INSURANCE_ORDER]: (state, action) => ({
    insurance: {
      ...state.insurance,
      insuranceOrderId: action.orderId,
      insuranceStartDate: action.startDate,
      insuranceEndDate: action.endDate,
    },
  }),
  [RESET_CART_ORDER]: () => ({
    order: undefined,
  }),
  [RESET_CART_INSURANCE]: () => ({
    insurance: getCartInsuranceInitialState(),
  }),
  [CART_PACKAGE_UPSELL_SHOWN]: (state) => ({
    accommodation: {
      ...state.accommodation,
      packageUpsellShown: true,
    },
  }),
  [CART_ADD_GIFT_CARD]: (state, action) => resetTransaction({
    ...state,
    useLeCredit: false,
    giftCard: {
      ...state.giftCard,
      price: action.price,
    },
  }),
  [CART_ADD_PAYMENT_BALANCE]: (state, action) => resetTransaction({
    ...state,
    useLeCredit: false,
    paymentBalance: {
      ...state.paymentBalance,
      price: action.price,
    },
  }),
  [UPDATE_PERSONALISE_GIFT_CARD_DATA]: (state, action) => ({
    giftCard: {
      ...state.giftCard,
      personalisation: action.data,
    },
  }),
  [CART_INITIALISED]: () => ({
    initialised: true,
  }),
  [CART_UPDATE_ACCOMMODATION_PRICE]: (state, action) => ({
    items: state.items.map(item => ({
      ...item,
      price: action.price ?? item.price,
      reservation: {
        ...item.reservation,
        extraGuestSurcharge: action?.extraGuestSurcharges?.[item.id] ?? item.reservation.extraGuestSurcharge,
        durationSurchargeTotal: action.surcharge ?? item.reservation.durationSurchargeTotal,
        taxesAndFees: action.taxesAndFees ?? 0,
        propertyFees: action.propertyFees ?? 0,
      },
    })),
  }),
  [CART_UPDATE_FLIGHT_ORIGIN]: (state, action) => ({
    flight: {
      ...state.flight,
      flightOrigin: action.flightOrigin,
      flightOriginName: action.flightOriginName,
    },
  }),
  [CART_UPDATE_FLIGHT_DESTINATION]: (state, action) => ({
    flight: {
      ...state.flight,
      flightDestination: action.flightDestination,
      flightDestinationName: action.flightDestinationName,
    },
  }),
  [CART_ADD_BEDBANK]: (state, action) => ({
    bedbank: {
      ...state.bedbank,
      ...action.data,
    },
  }),
  [RESET_FLIGHT_SELECT]: (state) => ({
    ...state,
    isFlightSelectError: false,
  }),
  [CART_SET_EXPERIENCE_ITEM]: (state, action) => {
    return {
      experiences: {
        ...state.experiences,
        items: {
          ...state.experiences.items,
          [action.experience.id]: {
            ...state.experiences.items[action.experienceId],
            ...action.params,
            experience: action.experience,
            tickets: action.tickets,
            transactionKey: uuidV4(),
          },
        },
      },
    }
  },
  [CART_SET_EXPERIENCE_TRANSFER]: (state, action) => {
    const nextTransfer = {
      ...state.experiences.transfers[action.experience.id],
      experience: action.experience,
    }
    if (action.transfer.type === 'HOTEL-TO-AIRPORT') {
      nextTransfer.toAirport = {
        transactionKey: uuidV4(),
        transfer: action.transfer,
      }
    } else {
      nextTransfer.toHotel = {
        transactionKey: uuidV4(),
        transfer: action.transfer,
      }
    }

    return {
      experiences: {
        ...state.experiences,
        transfers: {
          ...state.experiences.transfers,
          [action.experience.id]: nextTransfer,
        },
      },
    }
  },
  [CART_REMOVE_EXPERIENCE_TRANSFER]: (state, action) => {
    let nextTransfer = { ...state.experiences.transfers[action.experienceId] }
    if (action.transferType === 'HOTEL-TO-AIRPORT') {
      delete nextTransfer.toAirport
    } else {
      delete nextTransfer.toHotel
    }

    if (!nextTransfer.toHotel && !nextTransfer.toAirport) {
      nextTransfer = undefined
    }

    return {
      experiences: {
        ...state.experiences,
        transfers: {
          ...state.experiences.transfers,
          [action.experienceId]: nextTransfer,
        },
      },
    }
  },
  [CART_SET_FLIGHTS_CREDIT_DATA]: (state, action) => ({
    flight: {
      ...state.flight,
      creditDetails: action.data,
    },
  }),
  [UPDATE_DEPARTING_FLIGHT_SEGMENT_COST]: (state, action) => resetTransaction({
    ...state,
    flight: {
      ...state.flight,
      journey: {
        ...state.flight.journey,
        departing: {
          ...state.flight.journey.departing,
          ...action.data,
        },
      },
    },
  }),
  [UPDATE_RETURNING_FLIGHT_SEGMENT_COST]: (state, action) => resetTransaction({
    ...state,
    flight: {
      ...state.flight,
      journey: {
        ...state.flight.journey,
        returning: {
          ...state.flight.journey.returning,
          ...action.data,
        },
      },
    },
  }),
  [API_CALL_SUCCESS]: (state, action) => apiSuccesses(action.api)(state, action),
  [API_CALL_REQUEST]: (state, action) => apiRequests(action.api)(state, action),
  [API_CALL_FAILURE]: (state, action) => apiFailures(action.api)(state, action),
})

const apiRequests = reducerSwitch<App.CartState>({
  [CART_RESTORE]: () => ({
    restored: false,
    restoredTotal: undefined,
    cartRestoring: true,
  }),
  [FETCH_PACKAGE_PRICES]: () => ({
    fetchingPackagePrices: true,
  }),
  [INITIALISING_CART]: () => ({
    initialised: false,
    initialiseError: undefined,
  }),
  [GET_FINAL_FLIGHT_FARES]: () => ({
    isFlightSelectLoading: true,
    isFlightSelectError: false,
    isFlightSelected: false,
  }),
  [FETCH_EXPERIENCE_CHECKOUT_TRAVELLER_FIELDS]: (state, action) => ({
    experiences: {
      ...state.experiences,
      travellerForms: {
        [action.key]: {
          error: undefined,
          forms: [],
          fields: [],
          fetching: true,
        },
      },
    },
  }),
  [FETCH_TAX_BREAKDOWN]: () => ({
    fetchingTaxBreakdown: true,
  }),
})

const apiFailures = reducerSwitch<App.CartState>({
  [CART_RESTORE]: () => ({
    restored: false,
    restoredTotal: undefined,
    cartRestoring: false,
  }),
  [FETCH_PACKAGE_PRICES]: () => ({
    fetchingPackagePrices: false,
  }),
  [INITIALISING_CART]: (state, action) => ({
    initialised: false,
    initialiseError: action.error,
  }),
  [GET_FINAL_FLIGHT_FARES]: () => ({
    isFlightSelectLoading: false,
    isFlightSelectError: true,
    isFlightSelected: false,
  }),
  [FETCH_EXPERIENCE_CHECKOUT_TRAVELLER_FIELDS]: (state, action) => ({
    experiences: {
      ...state.experiences,
      travellerForms: {
        ...state.experiences.travellerForms,
        [action.key]: {
          ...state.experiences.travellerForms[action.key],
          error: action.error,
          fetching: false,
        },
      },
    },
  }),
  [FETCH_TAX_BREAKDOWN]: (state) => ({
    fetchingTaxBreakdown: false,
    accommodation: {
      ...state.accommodation,
      taxBreakdown: {},
    },
  }),
})

const apiSuccesses = reducerSwitch<App.CartState>({
  [CART_RESTORE]: (state, action) => resetTransaction({
    ...initialState,
    ...action.data,
    restored: true,
  }, { preserveTransactionKeys: true }),
  [INITIALISING_CART]: () => ({
    initialised: true,
    initialiseError: undefined,
  }),
  [FETCH_INSURANCE_QUOTES]: (state, action) => ({
    insurance: {
      ...state.insurance,
      insuranceTravellers: action.travellers,
      insuranceStartDate: action.startDate,
      insuranceEndDate: action.endDate,
      destinationCountries: action.destinationCountries,
      numberOfSeniors: action.numberOfSeniors,
      insuranceQuote: action.data.find(q => q.productId === state.insurance.insuranceProductId),
    },
  }),
  [FETCH_PACKAGE_PRICES]: (state, action) => ({
    fetchingPackagePrices: false,
    accommodation: {
      ...state.accommodation,
      offer: action.data,
      package: action.data.packages.find(p => p.id === state.accommodation.package.id),
    },
  }),
  [FETCH_AVAILABLE_CREDIT]: (state, action) => ({
    credit: {
      ...state.credit,
      balance: action.data.balance,
      error: undefined,
    },
  }),
  [GET_FINAL_FLIGHT_FARES]: () => ({
    isFlightSelectLoading: false,
    isFlightSelected: true,
  }),
  [FETCH_FLIGHT_BAGGAGE]: (state, action) => ({
    flight: state.flight.journey.departing.journeyKey && state.flight.journey.returning.journeyKey ? {
      ...state.flight,
      journey: {
        ...state.flight.journey,
        departing: {
          ...state.flight.journey.departing,
          extras: {
            ...state.flight.journey.departing.extras,
            ...action.data.newBaggage[0],
          },
        },
        returning: {
          ...state.flight.journey.returning,
          extras: {
            ...state.flight.journey.returning.extras,
            ...action.data.newBaggage[1],
          },
        },
      },
    } : state.flight,
  }),
  [USER_LOGOUT]: () => initialState,
  [CART_UPDATE_BEDBANK_SESSION_ID]: (state, action) => ({
    bedbank: {
      ...state.bedbank,
      sessionId: action.data.sessionId,
      price: action.data.price,
    },
  }),
  [FETCH_BEDBANK_OFFER_RATES]: (state, action: ApiAction<Array<App.BedbankRate>>) => {
    const newRate = action.data.find(rate => rate.id === state.bedbank.rate?.id)
    if (newRate) {
      // got new information for the rate in the cart, update it
      return {
        bedbank: {
          ...state.bedbank,
          rate: newRate,
        },
      }
    }
  },
  [FETCH_EXPERIENCE_CHECKOUT_TRAVELLER_FIELDS]: (state, action) => ({
    experiences: {
      ...state.experiences,
      travellerForms: {
        [action.key]: {
          ...state.experiences.travellerForms[action.key],
          forms: action.data.travellerDetails,
          fields: action.data.customerDetails,
          fetching: false,
        },
      },
    },
  }),
  [FETCH_TAX_BREAKDOWN]: (state, action) => ({
    fetchingTaxBreakdown: false,
    accommodation: {
      ...state.accommodation,
      taxBreakdown: action.data,
    },
  }),
})
